import { useContext, useMemo } from 'react';

import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { isAssignmentGradingInPublicEnabled } from 'bundles/grading-common';
import submissionAttemptQuery from 'bundles/grading/api/SubmissionAttemptQuery.graphql';
import submissionAttemptQueryV2 from 'bundles/grading/api/SubmissionAttemptQueryV2.graphql';
import type { SubmissionAttemptResponseFragment } from 'bundles/grading/api/__generated__/SubmissionAttemptFragment';
import type {
  SubmissionAttemptV2Query,
  SubmissionAttemptV2QueryVariables,
} from 'bundles/grading/api/__generated__/SubmissionAttemptQueryV2';
import type { SubmissionAttemptQuery, SubmissionAttemptQueryVariables } from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { getScopedSubmitterIdFromRouteSubmitterId } from 'bundles/grading/utils/submitterUtils';

export type UseSubmissionAttemptResult = {
  loading: boolean;
  data: SubmissionAttemptResponseFragment['questions'];
  error?: ApolloError;
};

/**
 * This hook sends a query to fetch a list of submission attempts
 */
const useSubmissionAttemptQuery = (variables: SubmissionAttemptQueryVariables): UseSubmissionAttemptResult => {
  const { pageData, submissionData } = useContext(SGAPageContext);
  const routeSubmitterId = submissionData.data?.submitter?.id || '';
  const useApiV2 = isAssignmentGradingInPublicEnabled();

  const result = useQuery<SubmissionAttemptQuery, SubmissionAttemptQueryVariables>(submissionAttemptQuery, {
    variables,
    context: { clientName: 'gatewayGql' },
    skip: useApiV2,
    onError: (error) => {
      Sentry.captureException('Error fetching SubmissionAttempts in Quick Grader', {
        extra: { error, variables: JSON.stringify(variables) },
      });
    },
  });

  const attemptQuestions = useMemo(() => {
    if (result.loading) {
      return [];
    }
    return result.data?.GroupGrading?.querySubmissionAttempt?.questions;
  }, [result.data, result.loading]);

  const variablesV2 = {
    itemId: variables.itemId || '',
    courseId: pageData.courseId,
    scopedSubmitterId: getScopedSubmitterIdFromRouteSubmitterId(routeSubmitterId, pageData.gradingGroupId),
    submissionAttemptId: variables.submissionAttemptId,
  };

  const resultV2 = useQuery<SubmissionAttemptV2Query, SubmissionAttemptV2QueryVariables>(submissionAttemptQueryV2, {
    variables: variablesV2,
    context: { clientName: 'gatewayGql' },
    skip: !useApiV2,
    onError: (error) => {
      Sentry.captureException('Error fetching SubmissionHeaders V2 in Quick Grader', {
        extra: { error, variables: JSON.stringify(variables) },
      });
    },
  });

  const attemptQuestionsV2 = useMemo(() => {
    if (resultV2.loading) {
      return [];
    }
    return resultV2.data?.GroupGrading?.querySubmissionAttemptV2?.questions;
  }, [resultV2.data, resultV2.loading]);

  if (useApiV2) {
    return {
      loading: resultV2.loading,
      data: attemptQuestionsV2,
      error: resultV2.error,
    };
  }

  return {
    loading: result.loading,
    data: attemptQuestions,
    error: result.error,
  };
};

export default useSubmissionAttemptQuery;
